// The admin session: the dashboard's AuthContext shape over the admin
// endpoints, with the password form replaced by the Google redirect flow.
// Storage keys are `gb.admin.*` so a developer signed into both apps on
// localhost keeps two independent sessions.
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AdminSession, AdminUser } from "@gb/admin-contracts";
import type { AuthTokens } from "@gb/contracts";
import { api, setAccessToken, setTokenRefresher } from "../api/client";

const TOKENS_KEY = "gb.admin.tokens";
const ADMIN_KEY = "gb.admin.user";
/** The `state` echo — sessionStorage, so it dies with the tab. */
const STATE_KEY = "gb.admin.signin-state";

type Status = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: Status;
  admin: AdminUser | null;
  /** Kick off the Google redirect. Resolves only on failure. */
  startSignIn: () => Promise<string | null>;
  /** Finish the flow on /auth/callback. Returns an error message, or null. */
  completeSignIn: (code: string, state: string) => Promise<string | null>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);

  // Restore a persisted session on first paint.
  useEffect(() => {
    try {
      const t = localStorage.getItem(TOKENS_KEY);
      const a = localStorage.getItem(ADMIN_KEY);
      if (t && a) {
        const restored = JSON.parse(t) as AuthTokens;
        setTokens(restored);
        setAdmin(JSON.parse(a) as AdminUser);
        // Synchronously, before status flips — the first authenticated query
        // fires on the same paint as `authenticated`.
        setAccessToken(restored.accessToken);
        setStatus("authenticated");
        return;
      }
    } catch {
      // fall through to unauthenticated
    }
    setStatus("unauthenticated");
  }, []);

  // Off React state so the client's refresher always reads the current value.
  const refreshTokenRef = useRef<string | null>(null);
  refreshTokenRef.current = tokens?.refreshToken ?? null;

  useEffect(() => {
    setTokenRefresher(async () => {
      const refreshToken = refreshTokenRef.current;
      if (!refreshToken) return null;

      const res = await api.auth.refresh({ body: { refreshToken } }).catch(() => null);
      if (!res || res.status !== 200) {
        setTokens(null);
        setAdmin(null);
        setAccessToken(null);
        setStatus("unauthenticated");
        localStorage.removeItem(TOKENS_KEY);
        localStorage.removeItem(ADMIN_KEY);
        return null;
      }

      const next = res.body.tokens;
      setTokens(next);
      setAccessToken(next.accessToken);
      localStorage.setItem(TOKENS_KEY, JSON.stringify(next));
      return next.accessToken;
    });

    return () => setTokenRefresher(null);
  }, []);

  const startSignIn = async (): Promise<string | null> => {
    const res = await api.auth.googleAuthUrl().catch(() => null);
    if (!res || res.status !== 200) {
      return "The backoffice sign-in is not available right now.";
    }
    // Kept for the callback to compare — the browser finishing the flow must
    // be the one that started it.
    sessionStorage.setItem(STATE_KEY, res.body.state);
    window.location.assign(res.body.url);
    return null;
  };

  const completeSignIn = async (code: string, state: string): Promise<string | null> => {
    const started = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    if (!started || started !== state) {
      return "That sign-in attempt didn’t start in this tab. Try again.";
    }

    const res = await api.auth.googleCallback({ body: { code, state } }).catch(() => null);
    if (!res || res.status !== 200) {
      return "That Google account cannot access the backoffice.";
    }

    const session = res.body as AdminSession;
    setTokens(session.tokens);
    setAdmin(session.admin);
    setAccessToken(session.tokens.accessToken);
    setStatus("authenticated");
    localStorage.setItem(TOKENS_KEY, JSON.stringify(session.tokens));
    localStorage.setItem(ADMIN_KEY, JSON.stringify(session.admin));
    return null;
  };

  const signOut = (): void => {
    const refreshToken = tokens?.refreshToken;
    setTokens(null);
    setAdmin(null);
    setAccessToken(null);
    setStatus("unauthenticated");
    localStorage.removeItem(TOKENS_KEY);
    localStorage.removeItem(ADMIN_KEY);
    if (refreshToken) {
      void api.auth.logout({ body: { refreshToken } }).catch(() => undefined);
    }
  };

  return (
    <AuthContext.Provider value={{ status, admin, startSignIn, completeSignIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
